import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import SubTitle from '../SubTitle';
import { colors } from '../../constants/styleConstants';
import NextLink from '../NextLink';
import ProLabel from '../ProLabel';

const useStyles = makeStyles(theme => ({
  sideBar: {
    backgroundColor: colors.background.gray,
    fontWeight: 'normal',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  section: {
    padding: 0,
    paddingTop: 30,
    margin: 0,
    '& li': {
      listStyleType: 'none',
    },
    '&:last-child': {
      padding: '30px 0',
    },
  },
  sectionTitle: {
    padding: '18px 10px',
    margin: 0,
  },
  activeNavTab: {
    backgroundColor: colors.background.biscue,
    color: `${colors.primary} !important`,
    fontWeight: 'bold',
  },
  sectionItems: {
    fontWeight: 'lighter',
  },
  sectionItem: {
    color: colors.title,
    display: 'block',
    textDecoration: 'none',
    fontSize: 15,
    lineHeight: '28px',
    padding: '0 24px',
    '&:hover': {
      backgroundColor: colors.background.biscue,
      color: `${colors.primary} !important`,
      fontWeight: 'normal',
    },
    '&:focus': {
      backgroundColor: colors.background.biscue,
      color: `${colors.primary} !important`,
      fontWeight: 'bold',
    },
  },
  disabledLink: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  proBadge: {
    marginBottom: 8,
  },
}));

const SidebarSection = ({ children, subtitle }) => {
  const classes = useStyles();

  return (
    <ul className={classes.section}>
      <SubTitle type="common" className={classes.sectionTitle}>
        {subtitle}
      </SubTitle>
      <div className={classes.sectionItems}>{children}</div>
    </ul>
  );
};

SidebarSection.propTypes = {
  children: PropTypes.node,
  subtitle: PropTypes.string,
};

const SideBarContent = ({ t, sideBarData, activeTab }) => {
  const classes = useStyles();

  const isComponentShown = component => {
    const isShownProperty = get(component, 'isShown', true);
    return typeof isShownProperty === 'function' ? Boolean(isShownProperty()) : Boolean(isShownProperty);
  };

  return (
    <nav className={classes.sideBar}>
      {Object.keys(sideBarData).map(key => {
        const section = sideBarData[key];

        if (key === 'lang') {
          return (
            <SidebarSection key={key} subtitle={t(section.title)}>
              <li className={classes.sectionItem}>{section.component}</li>
            </SidebarSection>
          );
        }

        const sectionReducer = (accumulator, currentValue) => accumulator || isComponentShown(currentValue);
        const isSectionShown = section.items.reduce(sectionReducer, false);
        const isActiveLink = path => activeTab === path || activeTab.includes(`${path}/`);

        return isSectionShown ? (
          <SidebarSection key={key} subtitle={t(section.title)}>
            {section.items.map(link => {
              const isShown = isComponentShown(link);
              return isShown ? (
                <li key={link.title}>
                  {link?.forceReload ? (
                    <a
                      href={link.path}
                      className={classnames(classes.sectionItem, {
                        [classes.activeNavTab]: isActiveLink(link.path),
                        [classes.disabledLink]: link.disabled,
                      })}
                    >
                      {t(link.title)} {link.isPro && <ProLabel className={classes.proBadge} />}
                    </a>
                  ) : (
                    <NextLink
                      href={link.path}
                      className={classnames(classes.sectionItem, {
                        [classes.activeNavTab]: isActiveLink(link.path),
                        [classes.disabledLink]: link.disabled,
                      })}
                    >
                      {t(link.title)} {link.isPro && <ProLabel className={classes.proBadge} />}
                    </NextLink>
                  )}
                </li>
              ) : null;
            })}
          </SidebarSection>
        ) : null;
      })}
    </nav>
  );
};

SideBarContent.propTypes = {
  t: PropTypes.func.isRequired,
  sideBarData: PropTypes.object.isRequired,
  activeTab: PropTypes.string,
};

export default SideBarContent;
